import { ResampleIntervals } from '@toggle/chart';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useChartNavigate } from '~/hooks/use-chart-navigate/useChartNavigate';
import { useTerminalNavigate } from '~/hooks/use-terminal-navigate/useTerminalNavigate';
import { SimpleChart } from '~/modules/simple-chart/SimpleChart';
import { ChartData } from '~/stores/use-chat/assistant-response.type';
import { ChartSearchParamsAssetValue } from '~/utils/chart-search-utils/chart-search-utils';
import { ChartState } from '~/views/turbo-chart/hooks/use-chart-state/useChartState';
import { ValidChartSettings } from '~/views/turbo-chart/utils/chart-settings-utils.ts/chart-settings-utils';

export interface ChartMessageProps {
  data: ChartData;
}

export const ChartMessage = ({ data }: ChartMessageProps) => {
  const { t } = useTranslation('common');
  const navigateTerminal = useTerminalNavigate();
  const chartNavigate = useChartNavigate();

  const hasDateRanges = !!data.start || !!data.end;
  const chartSettings: ValidChartSettings = {
    assets: data.assets.map(d => ({ ...d, paneIndex: 0 })),
    resample: data.resample ?? ResampleIntervals.OneDay,
    horizon: !hasDateRanges ? data.horizon : undefined,
    domain: hasDateRanges
      ? [data.start || 0, data.end || new Date().toISOString()]
      : null,
    hidden: undefined,
    paneSettings: {
      0: {
        seriesType: data.series_type ?? 'line',
        //we want to display all in single y axis, for now
        //in future use data.y_axis_type
        yAxisType: 'merged',
      },
    },
  };

  const navigateToChart = (chartState: ChartState) => {
    const assets: ChartSearchParamsAssetValue[] =
      chartState.chartPanes[0].chartAssetData.map(chartAssetDataPoint => {
        return [
          chartAssetDataPoint.entity.tag,
          chartAssetDataPoint?.snakeMeta?.snake ?? '',
          0,
        ];
      });

    navigateTerminal({
      navigateFn: () =>
        chartNavigate(assets, {
          resample: chartState.resample,
          horizon: chartState.horizon,
          domain: chartState.domain,
          seriesType: chartState.chartPanes[0].seriesType,
          hidden: chartState.hidden,
          priceDisplay: chartState.chartPanes[0].priceDisplay,
          yAxisType: chartState.chartPanes[0].yAxisType,
        }),
      crumb: {
        label: t('common:sideNavigation.chart'),
        isAccessibleFromSideBar: true,
      },
    });
  };

  return (
    <SimpleChart
      initialChartSettings={chartSettings}
      isChatMessage
      hasLineColourBasedOnPrice={false}
      fullScreenSetting={{ multiplePanes: true }}
      chartConfig={{
        enableHover: false,
        lineGradient: undefined,
      }}
      onClickFullscreen={navigateToChart}
    />
  );
};
